import client from "./client";

export type AgentSeverity = "info" | "warning" | "critical";
export type AgentRunStatus = "running" | "success" | "failed";

export interface AgentRead {
  name: string;
  display_name: string;
  description: string;
  version: string;
  default_cron: string | null;
  default_target_roles: string[];
  enabled: boolean;
  schedule_cron: string | null;
  config: Record<string, unknown>;
  last_run_at: string | null;
  next_run_at: string | null;
}

export interface AgentRunRead {
  id: string;
  org_id: string;
  agent_name: string;
  agent_version: string;
  status: AgentRunStatus;
  trigger_type: string;
  triggered_by: string | null;
  input_summary: Record<string, unknown> | null;
  output_summary: Record<string, unknown> | null;
  tokens_used: number;
  cost_usd: string | number;
  started_at: string;
  completed_at: string | null;
  error_message: string | null;
  created_at: string;
}

export interface AgentNotificationRead {
  id: string;
  org_id: string;
  agent_run_id: string;
  agent_name: string;
  title: string;
  summary: string;
  full_content: Record<string, unknown> | null;
  severity: AgentSeverity;
  target_roles: string[];
  is_read: boolean;
  created_at: string;
}

export interface AgentRunTriggerResponse {
  job_id: string;
  agent_name: string;
  status: string;
}

export interface AgentConfigUpdate {
  enabled?: boolean;
  schedule_cron?: string | null;
  config?: Record<string, unknown>;
}

export async function listAgents(): Promise<AgentRead[]> {
  const { data } = await client.get("/agents");
  return data;
}

export async function getAgent(name: string): Promise<AgentRead> {
  const { data } = await client.get(`/agents/${name}`);
  return data;
}

export async function triggerAgentRun(
  name: string,
  config?: Record<string, unknown>
): Promise<AgentRunTriggerResponse> {
  const { data } = await client.post(`/agents/${name}/run`, { config: config ?? null });
  return data;
}

export async function updateAgentConfig(
  name: string,
  payload: AgentConfigUpdate
): Promise<AgentRead> {
  const { data } = await client.put(`/agents/${name}/config`, payload);
  return data;
}

export async function listAgentRuns(params: {
  agent_name?: string;
  status?: AgentRunStatus;
  limit?: number;
  offset?: number;
} = {}): Promise<AgentRunRead[]> {
  const { data } = await client.get("/agents/runs", { params });
  return data;
}

export async function getAgentRun(id: string): Promise<AgentRunRead> {
  const { data } = await client.get(`/agents/runs/${id}`);
  return data;
}

export async function listAgentNotifications(params: {
  unread_only?: boolean;
  limit?: number;
  offset?: number;
} = {}): Promise<AgentNotificationRead[]> {
  const { data } = await client.get("/agents/notifications", { params });
  return data;
}

export async function markAgentNotificationRead(id: string): Promise<void> {
  await client.put(`/agents/notifications/${id}/read`);
}
